/* Scrolls sideways when its columns' minimum widths exceed the panel (`DataTable` sets `.inner`'s
   min-width from them). Safe for sticky headers: those sit above their own `.scroller`. */
.table {
  min-width: 0;
  overflow-x: auto;
}

.inner {
  display: flex;
  flex-direction: column;
}

.frame {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* The scrollbar above the header (only while the table overflows), in step with the one below. */
.topScroll {
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid var(--c-border);
}

.head {
  display: grid;
  align-items: center;
  column-gap: 16px;
  height: 36px;
  padding: 0 var(--panel-pad-x);
  border-bottom: 1px solid var(--c-border);
}

.sticky {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--c-surface);
}

.headCell {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  /* Visible, not hidden: the resize handle sits across the column gap at the cell's right edge. */
  overflow: visible;
  font-size: var(--text-micro);
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--c-text-tertiary);
  white-space: nowrap;
  min-width: 0;
  user-select: none;
}

.sortButton {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  border-radius: 4px;
}

.sortButton:hover,
.sortButton:focus-visible,
.sorted {
  color: var(--c-text-secondary);
}

.sortButton:focus-visible {
  outline: 2px solid var(--c-brand);
  outline-offset: 2px;
}

/* -- User layout: drag a border to resize, a header to reorder ---------------------------------- */

/* The header cell is the drag handle for reordering; the border strip at its right edge resizes.
   Both are pointer-only (a touch here must not scroll the table), and the sort button inside keeps
   its own click and keyboard behaviour. */
.movable {
  position: relative;
  cursor: grab;
  touch-action: none;
}

.dragging {
  opacity: 0.45;
  cursor: grabbing;
}

/* The insertion point: a brand hairline on the edge the dragged header would land on. The
   header's own column gap (16px) is where it is drawn, centred, so nothing shifts. */
.dropBefore::before,
.dropAfter::after {
  content: '';
  position: absolute;
  top: 4px;
  bottom: 4px;
  width: 2px;
  border-radius: 1px;
  background: var(--c-brand);
  pointer-events: none;
}

.dropBefore::before {
  left: -9px;
}

.dropAfter::after {
  right: -9px;
}

/* Centred on the cell's grid line (`.ruled`), which sits at its right edge. */
.resizer {
  position: absolute;
  top: 0;
  bottom: 0;
  right: -6px;
  width: 12px;
  cursor: col-resize;
  touch-action: none;
  z-index: 1;
}

.resizer::after {
  content: '';
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 5px;
  width: 2px;
  border-radius: 1px;
  background: transparent;
  transition: background 0.12s ease;
}

.resizer:hover::after {
  background: var(--c-border-strong);
}

.resizer:active::after {
  background: var(--c-brand);
}

/* -- Column grid lines -------------------------------------------------------------------------

   Very faint, so the table still reads as rows, but enough to show where one column ends and so
   where to grab. Drawn by the cell itself: it reaches half the column gap into the gap on its right
   (a negative margin, given back as padding, so its content box is exactly as wide as before) and
   puts a 1px border there, centred between the two columns. The header's line is a little
   stronger, because that is where the grab is. Every column but the last is ruled. */
.ruled {
  margin-right: -8px;
  padding-right: 8px;
  border-right: 1px solid color-mix(in srgb, var(--c-border) 75%, transparent);
}

.head .ruled {
  border-right-color: var(--c-border-strong);
}

/* The row's tint is a variable so a pinned cell, which needs an opaque background of its own, can
   paint the same tint over it. */
.row {
  --row-bg: transparent;
  display: grid;
  align-items: center;
  column-gap: 16px;
  padding: 0 var(--panel-pad-x);
  border-bottom: 1px solid var(--c-border);
  font-size: var(--text-caption);
  outline: none;
  background: var(--row-bg);
}

.row:last-child {
  border-bottom: 0;
}

/* `striped`: every other row a shade off the panel, and a hover tint on every row. Declared before
   the selected/glow/picked tints so those still win. */
.stripe {
  --row-bg: color-mix(in srgb, var(--c-text-primary) 3%, transparent);
}

.clickable {
  cursor: pointer;
}

.hoverable:hover,
.clickable:hover,
.clickable:focus-visible {
  --row-bg: var(--c-overlay);
}

.clickable:focus-visible {
  box-shadow: inset 2px 0 0 var(--c-brand);
}

.selected,
.selected:hover {
  --row-bg: var(--c-brand-subtle);
  box-shadow: inset 2px 0 0 var(--c-brand);
}

.glow {
  --row-bg: color-mix(in srgb, var(--c-brand) 7%, transparent);
  box-shadow: inset 2px 0 0 var(--c-brand-vivid);
}

/* Picked for a bulk action: tinted, without the "open" bar `.selected` draws. */
.picked,
.picked:hover {
  --row-bg: color-mix(in srgb, var(--c-brand) 10%, transparent);
}

.muted {
  color: var(--c-text-tertiary);
}

.cell {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.right {
  text-align: right;
  justify-content: flex-end;
}

.center {
  text-align: center;
  justify-content: center;
}

.chevron {
  color: var(--c-text-tertiary);
}

.scroller {
  overflow: auto;
}

/* A header longer than its column ends in an ellipsis instead of running into the next one (a
   watchlist's strategy names); the full text is the cell's title. */
.headText {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The ⓘ keeps its place and size while the header text beside it truncates, and is not a grab:
   pressing it opens the explanation rather than starting a column drag. */
.headInfo {
  display: inline-flex;
  flex: none;
  cursor: pointer;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
}

/* The sort arrow keeps its size while the text beside it truncates. */
.headText + * {
  flex: none;
}

/* The checkbox column: the box shows on hover or focus, and on every row once anything is picked. */
.checkCell {
  display: flex;
  align-items: center;
}

.check {
  opacity: 0;
  transition: opacity 0.12s;
}

.row:hover .check,
.check:focus-visible,
.check:checked,
.picking .check {
  opacity: 1;
}

/* No hover on a touch screen, so the boxes would never appear: there they are always shown. */
@media (hover: none) {
  .check {
    opacity: 1;
  }
}

/* Kept in view while the table scrolls sideways. Opaque, with the row's tint painted over it. */
.pinned {
  position: sticky;
  left: 0;
  z-index: 1;
  background: linear-gradient(var(--row-bg, transparent), var(--row-bg, transparent)), var(--c-surface);
  align-self: stretch;
  display: flex;
  align-items: center;
}

.head .pinned {
  background: var(--c-surface);
}

/* Once scrolled, a hairline says the pinned column has content passing under it. */
.scrolledX .pinned:not(.checkCell) {
  box-shadow: 1px 0 0 var(--c-border);
}
